import { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';

interface TabSelectionChangeDetail {
  id: string | number;
  selected: boolean;
}

export const useTabAppVisibilityChanged = (
  instanceId?: string | number
): boolean => {
  const [isSelected, setIsSelected] = useState(() => {
    if (typeof document === 'undefined' || instanceId == null) return false;

    const element = document.getElementById(String(instanceId));
    return Boolean(element?.classList.contains('hx-tab__selected'));
  });
  const instanceIdRef = useRef(instanceId);
  const isSelectedRef = useRef(isSelected);

  useEffect(() => {
    instanceIdRef.current = instanceId;
    isSelectedRef.current = isSelected;
  }, [instanceId, isSelected]);

  useEffect(() => {
    const handleTabSelectionChange = (
      event: CustomEvent<TabSelectionChangeDetail>
    ) => {
      if (instanceIdRef.current == null) return;
      if (String(instanceIdRef.current) !== String(event.detail.id)) return;

      const { selected } = event.detail;
      if (isSelectedRef.current === selected) return;

      isSelectedRef.current = selected;
      setIsSelected(selected);
    };

    window.addEventListener(
      'TAB_APP_SELECTED_ON_CHANGE',
      handleTabSelectionChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'TAB_APP_SELECTED_ON_CHANGE',
        handleTabSelectionChange as EventListener
      );
    };
  }, []);

  return isSelected;
};

export const useWindowSize = () => {
  const isClient = typeof window === 'object';

  const getSize = useCallback(() => {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined,
    };
  }, [isClient]);

  const [windowSize, setWindowSize] = useState(getSize);
  const debouncedHandlerRef = useRef(null);
  const getSizeRef = useRef(getSize);

  // Keep ref in sync with current getSize
  useEffect(() => {
    getSizeRef.current = getSize;
  }, [getSize]);

  useEffect(() => {
    if (!isClient) {
      return;
    }

    function handleResize() {
      setWindowSize(getSizeRef.current());
    }

    if (!debouncedHandlerRef.current) {
      debouncedHandlerRef.current = debounce(handleResize, 500);
    }

    const handleResizeFn = debouncedHandlerRef.current;

    window.addEventListener('resize', handleResizeFn);
    return () => {
      window.removeEventListener('resize', handleResizeFn);
      if (handleResizeFn.cancel) {
        handleResizeFn.cancel();
      }
    };
  }, [isClient]);

  return windowSize;
};
